import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type {
	FilterDefinition,
	RangeFilterDefinition,
	FilterState,
	RangeFilterState,
	SingleValueFilterState,
	MultiValueFilterState,
	UseFilterStateParams,
	UseFilterStateReturn,
} from '../types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getDefaultRange(
	def: RangeFilterDefinition,
	dynamicRanges?: UseFilterStateParams['dynamicRanges']
): [number, number] {
	if (def.initialRangeKey && dynamicRanges?.[def.initialRangeKey]) {
		const r = dynamicRanges[def.initialRangeKey]!;
		return [r.min, r.max];
	}
	return [0, 999];
}

function buildInitialState(
	definitions: FilterDefinition[],
	queryParams: URLSearchParams,
	dynamicRanges?: UseFilterStateParams['dynamicRanges']
): Record<string, FilterState> {
	const state: Record<string, FilterState> = {};

	for (const def of definitions) {
		switch (def.type) {
			case 'range': {
				const rangeDef = def as RangeFilterDefinition;
				const initialRange = getDefaultRange(rangeDef, dynamicRanges);
				const urlMin = queryParams.get(rangeDef.urlKeys.min);
				const urlMax = queryParams.get(rangeDef.urlKeys.max);

				state[def.key] = {
					value: [
						urlMin ? Number(urlMin) : initialRange[0],
						urlMax ? Number(urlMax) : initialRange[1],
					],
					initialRange,
				} as RangeFilterState;
				break;
			}
			case 'radio':
			case 'search-input': {
				const urlValue = queryParams.get(def.urlKeys.value) || '';
				state[def.key] = { value: urlValue } as SingleValueFilterState;
				break;
			}
			case 'checkbox':
			case 'select': {
				const urlValue = queryParams.get(def.urlKeys.value);
				const arr = urlValue ? urlValue.split(',').filter(Boolean) : [];
				state[def.key] = { value: arr } as MultiValueFilterState;
				break;
			}
		}
	}

	return state;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useFilterState({
	definitions,
	queryParams,
	navigate,
	dynamicRanges,
}: UseFilterStateParams): UseFilterStateReturn {
	const definitionMap = useMemo(
		() => new Map(definitions.map((d) => [d.key, d])),
		[definitions]
	);

	const [state, setState] = useState<Record<string, FilterState>>(() =>
		buildInitialState(definitions, queryParams, dynamicRanges)
	);

	// Track previous dynamic ranges to detect actual changes
	const prevDynamicRangesRef = useRef<string>('');

	// ── Sync state FROM URL when queryParams change ──────────────────────────
	useEffect(() => {
		setState((prev) => {
			const next = { ...prev };
			let changed = false;

			for (const def of definitions) {
				switch (def.type) {
					case 'range': {
						const rangeDef = def as RangeFilterDefinition;
						const prevState = prev[def.key] as RangeFilterState | undefined;
						const initialRange =
							prevState?.initialRange ||
							getDefaultRange(rangeDef, dynamicRanges);
						const urlMin = queryParams.get(rangeDef.urlKeys.min);
						const urlMax = queryParams.get(rangeDef.urlKeys.max);
						const newValue: [number, number] = [
							urlMin ? Number(urlMin) : initialRange[0],
							urlMax ? Number(urlMax) : initialRange[1],
						];
						if (
							!prevState ||
							prevState.value[0] !== newValue[0] ||
							prevState.value[1] !== newValue[1]
						) {
							next[def.key] = { value: newValue, initialRange };
							changed = true;
						}
						break;
					}
					case 'radio':
					case 'search-input': {
						const urlValue = queryParams.get(def.urlKeys.value) || '';
						const prevValue = (prev[def.key] as SingleValueFilterState)?.value;
						if (prevValue !== urlValue) {
							next[def.key] = { value: urlValue };
							changed = true;
						}
						break;
					}
					case 'checkbox':
					case 'select': {
						const urlValue = queryParams.get(def.urlKeys.value);
						const arr = urlValue ? urlValue.split(',').filter(Boolean) : [];
						const prevArr =
							(prev[def.key] as MultiValueFilterState)?.value || [];
						if (JSON.stringify(prevArr) !== JSON.stringify(arr)) {
							next[def.key] = { value: arr };
							changed = true;
						}
						break;
					}
				}
			}

			return changed ? next : prev;
		});
	}, [queryParams, definitions, dynamicRanges]);

	// ── Sync initial ranges when dynamic ranges change ───────────────────────
	useEffect(() => {
		const rangesKey = JSON.stringify(dynamicRanges);
		if (rangesKey === prevDynamicRangesRef.current) return;
		prevDynamicRangesRef.current = rangesKey;

		if (!dynamicRanges) return;

		setState((prev) => {
			const next = { ...prev };
			let changed = false;

			for (const def of definitions) {
				if (def.type !== 'range') continue;
				const rangeDef = def as RangeFilterDefinition;
				if (
					!rangeDef.initialRangeKey ||
					!dynamicRanges[rangeDef.initialRangeKey]
				)
					continue;

				const newInitial = getDefaultRange(rangeDef, dynamicRanges);
				const prevState = prev[def.key] as RangeFilterState | undefined;

				if (
					!prevState ||
					prevState.initialRange[0] !== newInitial[0] ||
					prevState.initialRange[1] !== newInitial[1]
				) {
					const urlMin = queryParams.get(rangeDef.urlKeys.min);
					const urlMax = queryParams.get(rangeDef.urlKeys.max);
					next[def.key] = {
						value: [
							urlMin ? Number(urlMin) : newInitial[0],
							urlMax ? Number(urlMax) : newInitial[1],
						],
						initialRange: newInitial,
					};
					changed = true;
				}
			}

			return changed ? next : prev;
		});
	}, [dynamicRanges, definitions, queryParams]);

	// ── URL mutation helpers ─────────────────────────────────────────────────

	const pushToUrl = useCallback(
		(
			mutations: Array<{
				action: 'set' | 'delete';
				key: string;
				value?: string;
			}>
		) => {
			const params = new URLSearchParams(queryParams.toString());
			for (const m of mutations) {
				if (m.action === 'set' && m.value !== undefined) {
					params.set(m.key, m.value);
				} else {
					params.delete(m.key);
				}
			}
			params.set('page', '1');
			navigate({ search: params.toString() });
		},
		[queryParams, navigate]
	);

	// ── Public API ───────────────────────────────────────────────────────────

	const setFilter = useCallback(
		(key: string, value: any) => {
			setState((prev) => {
				const def = definitionMap.get(key);
				if (!def) return prev;

				const next = { ...prev };
				switch (def.type) {
					case 'range':
						next[key] = {
							...(prev[key] as RangeFilterState),
							value: value as [number, number],
						};
						break;
					case 'radio':
					case 'search-input':
						next[key] = { value: value as string };
						break;
					case 'checkbox':
					case 'select':
						next[key] = { value: value as string[] };
						break;
				}
				return next;
			});
		},
		[definitionMap]
	);

	const applyFilter = useCallback(
		(key: string) => {
			const def = definitionMap.get(key);
			const filterState = state[key];
			if (!def || !filterState) return;

			switch (def.type) {
				case 'range': {
					const [min, max] = (filterState as RangeFilterState).value;
					pushToUrl([
						{ action: 'set', key: def.urlKeys.min, value: String(min) },
						{ action: 'set', key: def.urlKeys.max, value: String(max) },
					]);
					break;
				}
				case 'radio':
				case 'search-input': {
					const val = (filterState as SingleValueFilterState).value;
					if (val) {
						pushToUrl([{ action: 'set', key: def.urlKeys.value, value: val }]);
					} else {
						pushToUrl([{ action: 'delete', key: def.urlKeys.value }]);
					}
					break;
				}
				case 'checkbox':
				case 'select': {
					const arr = (filterState as MultiValueFilterState).value;
					if (arr.length > 0) {
						pushToUrl([
							{ action: 'set', key: def.urlKeys.value, value: arr.join(',') },
						]);
					} else {
						pushToUrl([{ action: 'delete', key: def.urlKeys.value }]);
					}
					break;
				}
			}
		},
		[definitionMap, state, pushToUrl]
	);

	/**
	 * Sets the filter value AND immediately applies it to the URL.
	 * Use this for filters that should apply on change (radio, checkbox)
	 * instead of setFilter + applyFilter which suffers from stale closures.
	 */
	const setAndApply = useCallback(
		(key: string, value: any) => {
			const def = definitionMap.get(key);
			if (!def) return;

			// Update local state
			setState((prev) => {
				const next = { ...prev };
				switch (def.type) {
					case 'range':
						next[key] = {
							...(prev[key] as RangeFilterState),
							value: value as [number, number],
						};
						break;
					case 'radio':
					case 'search-input':
						next[key] = { value: value as string };
						break;
					case 'checkbox':
					case 'select':
						next[key] = { value: value as string[] };
						break;
				}
				return next;
			});

			// Immediately push to URL using the NEW value (not stale state)
			switch (def.type) {
				case 'range': {
					const [min, max] = value as [number, number];
					pushToUrl([
						{ action: 'set', key: def.urlKeys.min, value: String(min) },
						{ action: 'set', key: def.urlKeys.max, value: String(max) },
					]);
					break;
				}
				case 'radio':
				case 'search-input': {
					const val = value as string;
					if (val) {
						pushToUrl([{ action: 'set', key: def.urlKeys.value, value: val }]);
					} else {
						pushToUrl([{ action: 'delete', key: def.urlKeys.value }]);
					}
					break;
				}
				case 'checkbox':
				case 'select': {
					const arr = value as string[];
					if (arr.length > 0) {
						pushToUrl([
							{ action: 'set', key: def.urlKeys.value, value: arr.join(',') },
						]);
					} else {
						pushToUrl([{ action: 'delete', key: def.urlKeys.value }]);
					}
					break;
				}
			}
		},
		[definitionMap, pushToUrl]
	);

	const resetFilter = useCallback(
		(key: string) => {
			const def = definitionMap.get(key);
			if (!def) return;

			// Remove from URL
			const urlKeysToDelete = Object.values(def.urlKeys);
			pushToUrl(
				urlKeysToDelete.map((k) => ({ action: 'delete' as const, key: k }))
			);

			// Reset local state
			setState((prev) => {
				const next = { ...prev };
				switch (def.type) {
					case 'range': {
						const prevRange = prev[key] as RangeFilterState;
						next[key] = {
							value: [...prevRange.initialRange] as [number, number],
							initialRange: prevRange.initialRange,
						};
						break;
					}
					case 'radio':
					case 'search-input':
						next[key] = { value: '' };
						break;
					case 'checkbox':
					case 'select':
						next[key] = { value: [] };
						break;
				}
				return next;
			});
		},
		[definitionMap, pushToUrl]
	);

	const resetAll = useCallback(
		(defaultShape?: string) => {
			const newParams = new URLSearchParams();
			if (defaultShape) {
				newParams.set('shape', defaultShape);
			}
			newParams.set('page', '1');
			navigate({ search: newParams.toString() });

			// State will sync via the URL effect
		},
		[navigate]
	);

	const isVisible = useCallback(
		(key: string) => {
			const def = definitionMap.get(key);
			if (!def) return false;

			// Range filters: hide when backend sent no range for this dimension (null/undefined)
			if (def.type === 'range') {
				const rangeDef = def as RangeFilterDefinition;
				if (
					rangeDef.initialRangeKey &&
					!dynamicRanges?.[rangeDef.initialRangeKey]
				) {
					return false;
				}
			}

			// Dimension filters (height, width, OD, ID, wall): hide when "all shapes" or more than one shape selected
			if (def.visibleWhen?.shape && def.type === 'range') {
				const shapeState = state['shape'] as MultiValueFilterState | undefined;
				const shapeValue = shapeState?.value ?? [];
				const count = Array.isArray(shapeValue) ? shapeValue.length : 0;
				if (count === 0 || count > 1) {
					return false;
				}
			}

			// If filter depends on another, hide when dependency has no value
			if (def.dependsOn) {
				const depState = state[def.dependsOn];
				if (!depState) return false;
				const depValue = 'value' in depState ? depState.value : '';
				const isEmpty =
					!depValue || (Array.isArray(depValue) && depValue.length === 0);
				if (isEmpty) return false;
			}

			if (!def.visibleWhen) return true;

			return Object.entries(def.visibleWhen).every(
				([depKey, allowedValues]) => {
					const depState = state[depKey];
					if (!depState) return false;
					const currentValue = 'value' in depState ? depState.value : '';
					if (typeof currentValue === 'string') {
						return allowedValues.includes(currentValue);
					}
					// For array values, check if any matches
					if (Array.isArray(currentValue)) {
						return currentValue.some((v) => allowedValues.includes(v));
					}
					return false;
				}
			);
		},
		[definitionMap, state, dynamicRanges]
	);

	const getDefinition = useCallback(
		(key: string) => definitionMap.get(key),
		[definitionMap]
	);

	return {
		state,
		setFilter,
		applyFilter,
		setAndApply,
		resetFilter,
		resetAll,
		isVisible,
		getDefinition,
	};
}
